import { MainNavMenuAsButton, MainNavMenuLink } from "@compono/ui";
import get from "lodash.get";
import { useFlags } from "launchdarkly-react-client-sdk";

import { ShowIf, usePermissions } from "~/auth-layer";
import { useInterviewGuidesEnabled } from "~/components/InterviewGuides/hooks";

import { SettingsOutline2Icon } from "../icons/SettingsOutline2Icon";

// Admin Settings permissions that surface the Settings menu on their own.
const SETTINGS_PERMISSIONS = [
  "organisations.edit",
  "applicationMessages.createTemplates",
  "isAbleToManageTags",
  "jobBoards.manageSubscription",
  "organisations.manageApiKeys",
  "organisations.manageHrIntegrations",
  "organisations.manageSso",
  "referenceChecks.manage",
];

export const AdminNav = () => {
  const { referenceChecks } = useFlags();
  const interviewGuidesEnabled = useInterviewGuidesEnabled();
  const [permissions] = usePermissions();

  // Interview Guides + Question Bank live under Settings and show whenever the
  // feature flag is on — decoupled from the admin Settings permissions below, so
  // any Hire user (incl. Hiring Managers, who hold none of them) can reach them.
  // No Hire-role logic here: Reviewers are kept out inside the IG pages
  // (GuideAccessGuard), not the nav.
  const showInterviewGuides = interviewGuidesEnabled;

  const hasAnySettingsPermission = SETTINGS_PERMISSIONS.some((permission) =>
    get(permissions, permission),
  );

  // Render the Settings menu when the user has any admin Settings permission,
  // or when they can see Interview Guides (e.g. a Hiring Manager with no other
  // Settings access still needs the menu to reach guides + the question bank).
  if (!hasAnySettingsPermission && !showInterviewGuides) return null;

  return (
    <MainNavMenuAsButton icon={SettingsOutline2Icon} label="Settings">
      <ShowIf allowedTo="organisations.edit">
        <MainNavMenuLink href={`/account`} label="General" />
      </ShowIf>
      <ShowIf allowedTo="applicationMessages.createTemplates">
        <MainNavMenuLink
          href={`/message-templates`}
          label="Message templates"
        />
      </ShowIf>
      <ShowIf allowedTo="isAbleToManageTags">
        <MainNavMenuLink href={`/manage-tags`} label="Tag library" />
      </ShowIf>
      {showInterviewGuides && (
        <>
          <MainNavMenuLink
            data-testid="side-nav-interview-guides-main-nav-link"
            href="/interview-guides"
            label="Interview guides"
          />
          <MainNavMenuLink
            data-testid="side-nav-question-bank-main-nav-link"
            href="/question-bank"
            label="Question bank"
          />
        </>
      )}
      <ShowIf allowedTo="jobBoards.manageSubscription">
        <MainNavMenuLink
          href={`/job-board-integration`}
          label="Job board integration"
        />
      </ShowIf>
      <ShowIf allowedTo="organisations.manageApiKeys">
        <MainNavMenuLink href={`/employer-api`} label="API management" />
      </ShowIf>
      <ShowIf allowedTo="organisations.manageHrIntegrations">
        <MainNavMenuLink
          href={`/integrations`}
          label="Integration management"
          id="integrations"
        />
      </ShowIf>
      <ShowIf allowedTo="organisations.manageSso">
        <MainNavMenuLink href={`/sso`} label="SSO" id="sso" />
      </ShowIf>
      {referenceChecks && (
        <ShowIf allowedTo="referenceChecks.manage">
          <MainNavMenuLink
            href={`/guide-questions`}
            label="Reference check questions"
          />
        </ShowIf>
      )}
    </MainNavMenuAsButton>
  );
};
